import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { transformErrors } from "helpers/utils";
import { Col, Form, Row } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";
import {
  CriarUnidadeMedidaDtoType,
  unidadeDeMedidaBreadcrumbItems,
} from "data/unidadesDeMedida";
import { usePermissionModal } from "contexts/PermissionModalContext";

const NovaUnidadeDeMedida = () => {
  const { getMutationErrorHandler } = usePermissionModal();

  const form = useForm<CriarUnidadeMedidaDtoType>({
    defaultValues: {
      sigla: "",
      descricao: "",
    },
  });

  const mutation = useMutation<any, any, CriarUnidadeMedidaDtoType>({
    mutationFn: (p: CriarUnidadeMedidaDtoType) => {
      return axios.post("/api/unidadeMedida", p);
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para cadastrar unidade de medida",
      (error) => {
        const formattedErrors = transformErrors(error.response?.data?.errors || {});
        for (const [field, error] of Object.entries(formattedErrors)) {
          form.setError(field as any, error as any);
        }
      }
    ),
  });

  const onSubmit: SubmitHandler<CriarUnidadeMedidaDtoType> = (data) => {
    mutation.mutate(data);
  };

  if (mutation.isSuccess) {
    const id = mutation.data.data.id;
    return <Navigate to={`/cadastros/unidades-de-medida/${id}`} />;
  }

  return (
    <div>
      <FormProvider {...form}>
        <PageBreadcrumb items={unidadeDeMedidaBreadcrumbItems()} />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Cadastrar uma unidade de medida</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Preencha as informações para cadastrar uma nova unidade
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button variant="phoenix-secondary" type="button">
                Descartar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                loadingPosition="start"
              >
                Salvar
              </Button>
            </div>
          </div>
          <Row className="g-5">
            <Col xs={12} xl={8}>
              <h4 className="mb-3">Informações da unidade</h4>
              <Row className="g-2">
                <Form.Group
                  as={Col}
                  xs={2}
                  xl={2}
                  className="mb-5"
                  controlId="sigla"
                >
                  <Form.Control
                    placeholder="Sigla da unidade"
                    {...form.register("sigla")}
                    required
                    isInvalid={!!form.formState.errors.sigla}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.sigla?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={8}
                  xl={10}
                  className="mb-5"
                  controlId="descricao"
                >
                  <Form.Control
                    placeholder="Descrição da unidade"
                    {...form.register("descricao")}
                    required
                    isInvalid={!!form.formState.errors.descricao}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.descricao?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </div>
  );
};

export default NovaUnidadeDeMedida;
